<template>
    <Teleport
        v-if="isMounted"
        to="body"
    >
        <Transition
            :enterActiveClass="$style.transition_enter_active"
            :enterFromClass="$style.transition_enter_from"
            :leaveActiveClass="$style.transition_leave_active"
            :leaveToClass="$style.transition_leave_to"
            appear
        >
            <ToasterContainer
                v-if="isToastVisible && toast"
                closeButton
                :class="$style.toast_container_override"
                :title="toast.title"
                :image="toast.image"
                :altImg="toast.description"
                :description="toast.description"
                :animation="toast.animation"
                :buttonText="toast.buttonText"
                :imageStyles="toast.imageStyles"
                :interfaceTarget="`${toast.type}-toast`"
                @onClose="toastStore.closeToast"
                @onAction="toastStore.acceptToast"
            />
        </Transition>
    </Teleport>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, onUnmounted, ref, watch } from 'vue'

import { useIsMounted } from '@/utils/vue-hooks/use-is-mounted'

import { useToastStore } from './store'
import ToasterContainer from './ToasterContainer.vue'

const props = defineProps<{
    isFullScreen: boolean
}>()

// load lottie only if animation is present

const toastStore = useToastStore()
const isMounted = useIsMounted()
const toasterTimeout = ref<ReturnType<typeof setTimeout> | null>(null)

const { isVisible, toast } = storeToRefs(toastStore)
const isToastVisible = computed(() => isVisible.value && (!props.isFullScreen || toast.value?.isFullScreenAllowed))
const closeToastOnTimer = () => {
    if (toastStore.toast?.showTimeoutMs) {
        toasterTimeout.value = setTimeout(() => {
            if (toastStore.isVisible) {
                toastStore.closeToast()
            }
            toasterTimeout.value = null
        }, toastStore.toast?.showTimeoutMs)
    }
}
watch(() => toastStore.toast, closeToastOnTimer)
onUnmounted(() => {
    if (toasterTimeout.value) {
        clearTimeout(toasterTimeout.value)
        toasterTimeout.value = null
    }
})
</script>

<style module>
.transition_enter_active,
.transition_leave_active {
    pointer-events: none;
    transition: all 0.4s cubic-bezier(0.17, 0.7, 0.17, 1);
    backdrop-filter: blur(16px);
}

.transition_enter_from,
.transition_leave_to {
    opacity: 0;
    transform: translateY(100%);
}

@media (--tablet-plus) {
    .transition_enter_from,
    .transition_leave_to {
        transform: translateX(100%);
    }
}

.toast_container_override {
    z-index: 9999;
    position: fixed;
    bottom: 0;
    width: 100%;
    padding: var(--gap-12);

    @media (--tablet-plus) {
        top: var(--gap-12);
        right: var(--gap-12);
        bottom: auto;
        width: 300px;
        padding: 0;
    }
}
</style>
